import * as Enums from '@/utils/enums';
import { FormHeader, FormItem } from '@/interfaces/api/models';
import { defaultDataResultForType } from '@/PageComponents/Form/form-helpers';

// Mirrors mobile's isDuplicateFormName (module-forms.page.ts): case-insensitive,
// trimmed match against the other display names already in the group.
export function isDuplicateFormName(candidateName: string, existingNames: (string | undefined | null)[]): boolean {
    const target = (candidateName || '').trim().toLowerCase();
    if (!target) return false;
    return existingNames.some((name) => (name || '').trim().toLowerCase() === target);
}

// A header counts as empty when every FormItem still holds its type's untouched-blank value -
// mirrors mobile's nonEmptyDraftIds/countEmptyFormsForMaster check. A plain "is DataResult
// non-blank" check is not enough: getBlankFormItemFromField (form-helpers.ts) seeds an untouched
// Checkbox field with the literal string "false", not "" - a real, non-blank string that would
// otherwise read as "filled in" even though the user never touched it.
export function isFormHeaderEmpty(formItems: FormItem[] | undefined | null): boolean {
    if (!formItems || formItems.length === 0) return true;
    return !formItems.some((item) => {
        const value = (item.DataResult ?? '').toString().trim();
        if (value.length === 0) return false;
        const blankDefault = (defaultDataResultForType(item.DataType) ?? '').toString().trim();
        return value !== blankDefault;
    });
}

// Mirrors mobile's ngOnInit parse of Common.Option_MaxFormDuplicateCount: any
// non-finite or non-positive value falls back to 20.
export function parseMaxDuplicateCount(optionValue: string | null | undefined, fallback = 20): number {
    const val = optionValue ? parseInt(optionValue, 10) : NaN;
    return Number.isFinite(val) && val > 0 ? val : fallback;
}

export function suggestedDuplicateName(baseName: string | undefined | null): string {
    return `${baseName ?? ''} Copy`.trim();
}

// Builds the new local FormHeader for a duplicate, same shape addSelectedFormDefinition
// already builds for a brand-new form: fresh ID, blank status/dates, _isNew so nothing
// persists until the editor's Save Draft.
export function buildDuplicateFormHeader(
    source: FormHeader,
    newID: string,
    newName: string,
    itemID: string,
    itemModule: number,
    associatedItemID: string | null,
): FormHeader {
    const formDefinition = source.FormDefinition;
    return {
        ID: newID,
        FormDefinitionID: source.FormDefinitionID,
        FormDefinition: formDefinition,
        Module: itemModule,
        ItemID: itemID,
        FormStatus: Enums.FormStatus.Draft,
        AssociatedModule: formDefinition?.Module,
        AssociatedItemID: associatedItemID ?? undefined,
        CompletedDate: null,
        Invalid: false,
        ExpireDate: null,
        FormDefinitionDisplayName: newName,
        FormDefinitionDescription: source.FormDefinitionDescription || formDefinition?.Description,
        _isNew: true,
    };
}
